import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { Mail, Lock, Cpu, Zap } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login({ email, password });
      navigate('/dashboard');
    } catch {
      // Error is handled by the store
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Background effects */}
      <div className="bg-mesh" />
      <div className="bg-noise" />
      
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        
        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-cyan-500/20 blur-[100px] animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-purple-500/20 blur-[120px] animate-float" style={{ animationDelay: '-3s' }} />
        <div className="absolute top-1/2 left-1/2 w-48 h-48 rounded-full bg-pink-500/15 blur-[80px] animate-float" style={{ animationDelay: '-1.5s' }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-16">
          <div className="opacity-0-init animate-fade-in-up">
            <div className="flex items-center gap-4 mb-8">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-glow-lg">
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 blur-xl opacity-50" />
              </div>
            </div>
            
            <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
              Protect Your
              <span className="block text-gradient">Software</span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-md">
              Enterprise-grade binary protection with advanced virtualization and obfuscation.
            </p>
            
            <div className="flex flex-wrap gap-3">
              {['VMProtect', 'Themida', 'Custom'].map((tag, i) => (
                <span
                  key={tag}
                  className="px-4 py-2 rounded-full glass text-sm font-medium text-slate-300 border border-slate-700/50 opacity-0-init animate-fade-in-up"
                  style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        {/* Decorative lines */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute inset-0 bg-grid opacity-10" />
        
        <div className="w-full max-w-md relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8 opacity-0-init animate-fade-in-up">
            <div className="inline-flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-glow">
                <Cpu className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gradient">CipherBin</span>
            </div>
          </div>

          <Card variant="bordered" className="opacity-0-init animate-fade-in-up animation-delay-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Welcome back</h2>
              <p className="text-slate-500">Sign in to your account to continue</p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm animate-[fade-in-up_0.3s_ease-out]">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-red-400 font-medium">{error}</p>
                    <button
                      type="button"
                      onClick={clearError}
                      className="text-red-400/60 text-sm hover:text-red-400 mt-2 underline transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<Mail className="w-4 h-4" />}
                required
              />

              <Input
                label="Password"
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<Lock className="w-4 h-4" />}
                required
              />

              <Button type="submit" className="w-full" loading={isLoading} glow>
                <Zap className="w-4 h-4" />
                Sign In
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-800/50 text-center">
              <p className="text-slate-500 text-sm">
                Don't have an account?{' '}
                <Link 
                  to="/register" 
                  className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                  onClick={clearError}
                >
                  Create one
                </Link>
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
